<template>
  <div ref="rootRef" class="workspace-role-select" :class="[`size-${size}`, { 'is-open': open, 'is-disabled': disabled }]">
    <button
      ref="triggerRef"
      type="button"
      class="role-trigger"
      :disabled="disabled || loading"
      :aria-expanded="open"
      aria-haspopup="listbox"
      @click="toggleOpen"
    >
      <span class="role-trigger-icon" :class="`role-icon--${normalizedValue}`" aria-hidden="true">
        <component :is="roleIcon(normalizedValue)" />
      </span>
      <span class="role-trigger-text">
        <span class="role-trigger-label">{{ currentLabel }}</span>
        <span class="role-trigger-hint">{{ t('usuarios.rolePicker.changeRole') }}</span>
      </span>
      <svg class="role-trigger-chevron" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.94a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
      </svg>
    </button>

    <Teleport to="body">
      <Transition name="role-panel">
        <div
          v-if="open"
          ref="panelRef"
          class="role-panel role-panel--teleported"
          role="listbox"
          :style="panelStyle"
          :aria-label="t('usuarios.rolePicker.panelTitle')"
        >
          <p class="role-panel-intro">{{ panelIntro }}</p>
          <button
            v-for="option in visibleOptions"
            :key="option.value"
            type="button"
            role="option"
            class="role-option"
            :class="[
              `role-option--${option.value.toLowerCase()}`,
              { 'role-option--active': option.value === normalizedValue }
            ]"
            :aria-selected="option.value === normalizedValue"
            @click="selectRole(option.value)"
          >
            <div class="role-option-header">
              <span class="role-option-icon" :class="`role-icon--${option.value.toLowerCase()}`">
                <component :is="roleIcon(option.value)" />
              </span>
              <div class="role-option-titles">
                <span class="role-option-label">{{ option.label }}</span>
                <span class="role-option-summary">{{ option.summary }}</span>
              </div>
              <span v-if="option.value === normalizedValue" class="role-option-check" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.895 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                </svg>
              </span>
            </div>
            <div class="role-option-body">
              <div v-if="option.access.length" class="role-list role-list--allow">
                <span class="role-list-title">{{ t('usuarios.rolePicker.accessLabel') }}</span>
                <ul>
                  <li v-for="(line, i) in option.access" :key="'a-' + i">{{ line }}</li>
                </ul>
              </div>
              <div v-if="option.denied.length" class="role-list role-list--deny">
                <span class="role-list-title">{{ t('usuarios.rolePicker.deniedLabel') }}</span>
                <ul>
                  <li v-for="(line, i) in option.denied" :key="'d-' + i">{{ line }}</li>
                </ul>
              </div>
            </div>
          </button>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick, h } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  modelValue: {
    type: String,
    default: 'MEMBER'
  },
  workspaceType: {
    type: String,
    default: 'TAREFAS'
  },
  showOwnerOption: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: 'default',
    validator: (v) => ['default', 'compact'].includes(v)
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const { t, tm } = useI18n()
const open = ref(false)
const rootRef = ref(null)
const triggerRef = ref(null)
const panelRef = ref(null)
const panelStyle = ref({})

const normalizedValue = computed(() => String(props.modelValue || 'MEMBER').toUpperCase())

const isCrm = computed(() => String(props.workspaceType || '').toUpperCase() === 'CRM')

const typeKey = computed(() => (isCrm.value ? 'crm' : 'task'))

const panelIntro = computed(() =>
  isCrm.value ? t('usuarios.rolePicker.panelIntroCrm') : t('usuarios.rolePicker.panelIntroTask')
)

function toMessageArray(val) {
  if (Array.isArray(val)) return val.filter(Boolean)
  if (typeof val === 'string' && val) return [val]
  return []
}

function mergeLists(roleKey, listName) {
  const base = toMessageArray(tm(`usuarios.rolePicker.${roleKey}.${listName}.common`))
  const typed = toMessageArray(tm(`usuarios.rolePicker.${roleKey}.${listName}.${typeKey.value}`))
  return [...base, ...typed]
}

function buildOption(roleKey, value) {
  return {
    value,
    label: t(`usuarios.rolePicker.${roleKey}.label`),
    summary: t(`usuarios.rolePicker.${roleKey}.summary`),
    access: mergeLists(roleKey, 'access'),
    denied: mergeLists(roleKey, 'denied')
  }
}

const visibleOptions = computed(() => {
  const opts = [
    buildOption('member', 'MEMBER'),
    buildOption('staff', 'ADMIN')
  ]
  if (props.showOwnerOption) {
    opts.push(buildOption('owner', 'OWNER'))
  }
  return opts
})

const currentLabel = computed(() => {
  const v = normalizedValue.value
  if (v === 'OWNER') return t('usuarios.owner')
  if (v === 'ADMIN') return t('usuarios.staff')
  return t('usuarios.member')
})

function roleIcon(role) {
  const r = String(role || 'MEMBER').toUpperCase()
  if (r === 'OWNER') {
    return () => h('svg', { fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 00-.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z' })
    ])
  }
  if (r === 'ADMIN') {
    return () => h('svg', { fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' })
    ])
  }
  return () => h('svg', { fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor' }, [
    h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' })
  ])
}

function updatePanelPosition() {
  const el = triggerRef.value
  if (!el) return

  const rect = el.getBoundingClientRect()
  const panelWidth = Math.min(380, window.innerWidth - 32)
  const gap = 8
  let left = rect.right - panelWidth
  if (left < 16) left = 16
  if (left + panelWidth > window.innerWidth - 16) {
    left = window.innerWidth - panelWidth - 16
  }

  const spaceBelow = window.innerHeight - rect.bottom - gap
  const spaceAbove = rect.top - gap
  const openBelow = spaceBelow >= 240 || spaceBelow >= spaceAbove
  const maxHeight = Math.max(180, Math.min(520, openBelow ? spaceBelow - 8 : spaceAbove - 8))

  panelStyle.value = {
    position: 'fixed',
    top: openBelow ? `${rect.bottom + gap}px` : `${rect.top - gap}px`,
    left: `${left}px`,
    width: `${panelWidth}px`,
    maxHeight: `${maxHeight}px`,
    zIndex: 12500,
    transform: openBelow ? 'none' : 'translateY(-100%)'
  }
}

async function toggleOpen() {
  if (props.disabled || props.loading) return
  open.value = !open.value
  if (open.value) {
    await nextTick()
    updatePanelPosition()
  }
}

function selectRole(value) {
  if (value !== normalizedValue.value) {
    emit('update:modelValue', value)
    emit('change', value)
  }
  open.value = false
}

function onDocumentClick(e) {
  if (!open.value) return
  const target = e.target
  if (rootRef.value?.contains(target) || panelRef.value?.contains(target)) return
  open.value = false
}

function bindPositionListeners() {
  window.addEventListener('resize', updatePanelPosition)
  window.addEventListener('scroll', updatePanelPosition, true)
}

function unbindPositionListeners() {
  window.removeEventListener('resize', updatePanelPosition)
  window.removeEventListener('scroll', updatePanelPosition, true)
}

watch(open, async (isOpen) => {
  if (isOpen) {
    await nextTick()
    updatePanelPosition()
    bindPositionListeners()
  } else {
    unbindPositionListeners()
  }
})

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  unbindPositionListeners()
})
</script>

<style scoped>
.workspace-role-select {
  position: relative;
  min-width: 200px;
}

.workspace-role-select.size-compact {
  min-width: 160px;
}

.role-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 12px;
  background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  color: var(--text-primary);
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;
}

.role-trigger:hover:not(:disabled) {
  border-color: var(--accent-primary, #5865f2);
  box-shadow: 0 4px 14px rgba(88, 101, 242, 0.15);
}

.workspace-role-select.is-open .role-trigger {
  border-color: var(--accent-primary, #5865f2);
  box-shadow: 0 0 0 3px rgba(88, 101, 242, 0.2);
}

.role-trigger:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.role-trigger-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  flex-shrink: 0;
}

.role-trigger-icon svg {
  width: 18px;
  height: 18px;
}

.role-icon--owner {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: #fff;
}

.role-icon--admin {
  background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
  color: #fff;
}

.role-icon--member {
  background: linear-gradient(135deg, #64748b 0%, #475569 100%);
  color: #fff;
}

.role-trigger-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.role-trigger-label {
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
}

.role-trigger-hint {
  font-size: 11px;
  color: var(--text-secondary);
  font-weight: 500;
}

.size-compact .role-trigger-icon {
  width: 30px;
  height: 30px;
}

.size-compact .role-trigger-label {
  font-size: 12px;
}

.role-trigger-chevron {
  width: 18px;
  height: 18px;
  color: var(--text-secondary);
  flex-shrink: 0;
  transition: transform 0.2s;
}

.workspace-role-select.is-open .role-trigger-chevron {
  transform: rotate(180deg);
}

.role-panel {
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
}

.role-panel--teleported {
  /* posição via inline style (fixed + Teleport) */
}

.role-panel-intro {
  margin: 0 4px 10px;
  font-size: 12px;
  line-height: 1.45;
  color: var(--text-secondary);
}

.role-option {
  display: block;
  width: 100%;
  margin-bottom: 8px;
  padding: 0;
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  background: var(--bg-primary);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;
}

.role-option:last-child {
  margin-bottom: 0;
}

.role-option:hover {
  border-color: var(--accent-primary, #5865f2);
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.role-option--active {
  border-color: var(--accent-primary, #5865f2);
  box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.25);
}

.role-option-header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 12px 8px;
}

.role-option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  flex-shrink: 0;
}

.role-option-icon svg {
  width: 20px;
  height: 20px;
}

.role-option-titles {
  flex: 1;
  min-width: 0;
}

.role-option-label {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
}

.role-option-summary {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--text-secondary);
}

.role-option-check {
  color: var(--accent-primary, #5865f2);
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

.role-option-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 12px 12px;
}

.role-list {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border-radius: 4px;
  font-size: 11px;
  line-height: 1.45;
}

.role-list--allow {
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.25);
}

.role-list--deny {
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.role-list-title {
  display: block;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
}

.role-list--allow .role-list-title {
  color: #059669;
}

.role-list--deny .role-list-title {
  color: #dc2626;
}

.role-list ul {
  margin: 0;
  padding-left: 16px;
}

.role-list li {
  margin-bottom: 3px;
  color: var(--text-primary);
}

.role-list li:last-child {
  margin-bottom: 0;
}

.role-panel-enter-active,
.role-panel-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.role-panel-enter-from,
.role-panel-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
